import {
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { getUnixTime } from "date-fns";
import { groupBy } from "lodash";
import { createNamespace } from "../redux/createNamespace";
import type { RootState } from "../redux/types";
import type { ChatMessage } from "./chatMessage.entity";

export function sortComparer(messageA: ChatMessage, messageB: ChatMessage) {
  const messageACreateTime = getTimeValue(messageA.create_time);
  const messageBCreateTime = getTimeValue(messageB.create_time);

  if (messageACreateTime === messageBCreateTime) {
    return 0;
  }

  return messageACreateTime - messageBCreateTime;
}

function getTimeValue(createTime: string | undefined) {
  if (typeof createTime !== "string") {
    return 0;
  }
  const date = new Date(createTime);
  return Number.isNaN(date.getTime()) ? 0 : getUnixTime(date);
}

const namespace = createNamespace("chatMessage");
const chatMessageAdapter = createEntityAdapter({
  selectId: (message: ChatMessage) => message.normalizeKey,
  sortComparer: sortComparer,
});
const initialState = chatMessageAdapter.getInitialState();

export const {
  reducer,
  actions: {
    updateChatMessageList,
    upsertChatMessage,
    upsertChatMessageList,
    removeChatMessage,
    removeChatMessageList,
    clearChatMessageList,
  },
} = createSlice({
  name: namespace,
  initialState,
  reducers: {
    updateChatMessageList: chatMessageAdapter.updateMany,
    upsertChatMessage: chatMessageAdapter.upsertOne,
    upsertChatMessageList: chatMessageAdapter.upsertMany,
    removeChatMessage: chatMessageAdapter.removeOne,
    removeChatMessageList: chatMessageAdapter.removeMany,
    clearChatMessageList: chatMessageAdapter.removeAll,
  },
});

export const createReducerType = (typename: string) =>
  `${namespace}/${typename}`;

const selectors = chatMessageAdapter.getSelectors();
export const chatMessageSelectors = {
  selectIds: selectors.selectIds,
  selectById: (
    ...params: Parameters<typeof selectors.selectById>
  ): ChatMessage | undefined => {
    return selectors.selectById(...params);
  },
  selectEntities: selectors.selectEntities,
  selectTotal: selectors.selectTotal,
  selectAll: selectors.selectAll,
};

export const chatMessageGroupByChannelIdSelectors = createSelector(
  (state: RootState["chatMessage"]) => chatMessageSelectors.selectAll(state),
  (messages) => {
    return groupBy(messages, "channel_id");
  },
);
